import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageSquareOff, X } from 'lucide-react';
import chatSs1 from '../../assets/chat_ss/1.jpeg';
import chatSs2 from '../../assets/chat_ss/2.jpeg';
import chatSs3 from '../../assets/chat_ss/3.jpeg';
import chatSs4 from '../../assets/chat_ss/4.jpeg';
import chatSs5 from '../../assets/chat_ss/5.jpeg';
import chatSs6 from '../../assets/chat_ss/6.webp';
import chatSs7 from '../../assets/chat_ss/7.webp';
import chatSs8 from '../../assets/chat_ss/8.webp';
import SectionDivider from './SectionDivider';

/**
 * The problem — named explicitly, so Features (directly above) reads as
 * "here's what solves that" rather than a feature dump with no stakes.
 *
 * The gallery is the actual point of this section: real WhatsApp ghosting
 * screenshots (client threads that just went silent) laid out in
 * scroll-parallax columns, each column drifting at its own speed/direction.
 *
 * 8 real screenshots exist in src/assets/chat_ss. Every one is shown
 * exactly once, no repeats — the grid is sized to the real count (8 slots,
 * 2 per column) rather than padded to a round number by cycling images.
 */

const vp = { once: false, amount: 0.5 } as const;

const FAILURES = ['No signed scope', 'No proof of delivery', 'No payment deadline', 'No leverage when they go quiet'];

const REAL_SCREENSHOTS = [chatSs1, chatSs2, chatSs3, chatSs4, chatSs5, chatSs6, chatSs7, chatSs8];

type GalleryItem = { id: number; src?: string };

// One slot per real screenshot — no cycling, no repeats.
const GALLERY_ITEMS: GalleryItem[] = REAL_SCREENSHOTS.map((src, i) => ({ id: i, src }));

function ScreenshotCard({ item }: { item: GalleryItem }) {
  return (
    <div className="relative w-[168px] sm:w-[200px] aspect-[9/16] shrink-0 rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
      {item.src ? (
        <img
          src={item.src}
          alt={`WhatsApp chat with a client who stopped replying after the freelance work was delivered (example ${item.id + 1})`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/15">
          <MessageSquareOff size={20} />
          <span className="font-mono text-[9px] uppercase tracking-widest">Screenshot</span>
        </div>
      )}
    </div>
  );
}

function ParallaxGallery() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start end', 'end start'] });

  // Four columns, alternating direction, varied distance — the thing that
  // makes a parallax grid read as alive instead of four identical lists.
  const yA = useTransform(scrollYProgress, [0, 1], [40, -100]);
  const yB = useTransform(scrollYProgress, [0, 1], [-90, 60]);
  const yC = useTransform(scrollYProgress, [0, 1], [60, -130]);
  const yD = useTransform(scrollYProgress, [0, 1], [-60, 90]);

  // 7 real screenshots, distributed 2/2/2/1 across four columns — no
  // repeats, so this re-splits by whatever GALLERY_ITEMS actually contains
  // rather than assuming a fixed count.
  const mid = Math.ceil(GALLERY_ITEMS.length / 4);
  const columns = [
    { items: GALLERY_ITEMS.slice(0, mid), y: yA, className: '' },
    { items: GALLERY_ITEMS.slice(mid, mid * 2), y: yB, className: 'mt-10' },
    { items: GALLERY_ITEMS.slice(mid * 2, mid * 3), y: yC, className: '' },
    { items: GALLERY_ITEMS.slice(mid * 3), y: yD, className: 'mt-16 hidden md:flex' },
  ];

  return (
    // No CSS mask-image here on purpose — masking a container full of scaled/
    // transformed <img> children forces some browsers into a lower-quality
    // compositing pass on the masked layer, which is what was actually
    // reading as "blurred" (the source photos themselves are sharp, 600–
    // 750px wide). Same fade effect via two plain gradient overlay divs
    // instead — solid, well-supported, doesn't touch the images at all.
    <div ref={containerRef} className="relative h-[460px] sm:h-[520px] md:h-[600px] overflow-hidden">
      <div className="flex items-start justify-center gap-4 sm:gap-5 h-full">
        {columns.map((col, i) => (
          <motion.div key={i} style={{ y: col.y }} className={`flex flex-col gap-4 sm:gap-5 ${col.className}`}>
            {col.items.map((item) => (
              <ScreenshotCard key={item.id} item={item} />
            ))}
          </motion.div>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 sm:h-28 bg-gradient-to-b from-black to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 sm:h-28 bg-gradient-to-t from-black to-transparent" />
    </div>
  );
}

export default function ProblemRevamp() {
  return (
    <>
      <SectionDivider />
      <section className="relative bg-black py-20 md:py-24 overflow-hidden">
        <div className="px-6 max-w-2xl mx-auto mb-12 md:mb-14 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            {/* Badge family (bordered pill, not a full-width rule) — this
              section is a narrow, centered prose column, not a grid, so it
              doesn't get the docket-row treatment How It Works/Features use.
              The one deliberate color break on the page: amber instead of
              brand green, naming what's actually wrong with a chat thread
              as evidence, not just labeling the section. */}
            {/* <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/25 px-3.5 py-1.5 mb-6 font-mono text-[10px] tracking-[0.25em] text-amber-400/90 uppercase">
            <span className="w-1 h-1 rounded-full bg-amber-400" />
            Finding of fact
          </span> */}
            <h2 className="font-bricolage font-extrabold text-3xl md:text-5xl text-white leading-[1.15] tracking-tight text-balance mb-5">
              Most gig work in India runs on a WhatsApp message and{' '}
              <span className="font-instrumentserif italic font-normal text-accent">good faith</span>.
            </h2>
            <p className="text-white/55 text-sm md:text-base leading-relaxed">
              No signed scope. No proof of what was delivered, or when. No agreed deadline for payment. So when a
              client goes quiet, you left holding a chat thread and no idea what to do next — this is what
              that actually looks like.
            </p>
          </motion.div>
        </div>

        <ParallaxGallery />

        <div className="px-6 max-w-3xl mx-auto text-center mt-12 md:mt-14">
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-6">
            {FAILURES.map((f, i) => (
              <motion.li
                key={f}
                className="flex items-center gap-2 text-[13px] text-white/55"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={vp}
                transition={{ delay: i * 0.08, duration: 0.3 }}
              >
                <span className="w-4 h-4 rounded-full bg-red-400/10 border border-red-400/25 flex items-center justify-center shrink-0">
                  <X size={9} className="text-red-400" strokeWidth={3} />
                </span>
                {f}
              </motion.li>
            ))}
          </ul>
          <p className="text-[13.5px] text-[#3cb44f] font-semibold">Every feature above exists to fix exactly this.</p>
        </div>
      </section>
    </>
  );
}
